import type {Feature} from "../feature";

/**
 * Generate a stable, deterministic ID for a feature based on its contents.
 *
 * Same feature (same atoms, same internal name) → same ID, regardless of when
 * the extractor is run or how features are ordered. This eliminates the
 * counter-based ephemeral IDs and enables ID-based cross-references in
 * serialized output.
 *
 * The ID is a 32-bit hex hash of `internalName|sortedAtomIndices`.
 */
export function stableFeatureId(feature: Feature): string {
    const sortedIndices = feature.atoms
        .map(a => a.index)
        .slice()
        .sort((a, b) => a - b);
    const key = `${feature.name}|${sortedIndices.join(",")}`;
    return `f-${hash32(key)}`;
}

/**
 * FNV-1a 32-bit hash. Fast, deterministic, no dependencies.
 * Returns 8-character lowercase hex.
 */
function hash32(input: string): string {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, "0");
}
